// ─────────────────────────────────────────────────────────────────────────────
// Data layer for the responder flow (POV: a company answering a questionnaire).
// Pure RPC — each function makes one HTTP call and returns the raw response.
// ─────────────────────────────────────────────────────────────────────────────

import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  CompanyDataPointAnswer,
  RawAnswerRequest,
  SaveAnswerItem,
  SubmitForReviewPayload,
} from './types';

/** The answer-request read migrated to `GET /api/questionnaires/requests/:id`
 *  (modules architecture), so the path **and** the base both changed and the
 *  detail now arrives inside a `{ data }` envelope. Pairing the new path with
 *  `QUESTIONNAIRE_SERVICE_URL` would produce
 *  `…/supplier-questionnaire-service/api/questionnaires/…`, a 404 that reads
 *  like a routing bug. `useAnswerRequestQuery` unwraps the envelope. */
export async function fetchAnswerRequest(
  id: string,
): Promise<{ data: RawAnswerRequest }> {
  return apiFetch<{ data: RawAnswerRequest }>(
    `/api/questionnaires/requests/${id}`,
    {},
    API_ROOT_URL,
  );
}

/** The answer write migrated to
 *  `POST /api/questionnaires/requests/:requestId/responses` (modules
 *  architecture), so the request id moved out of every payload item into the path
 *  and the base became `API_ROOT_URL`. Keeping `QUESTIONNAIRE_SERVICE_URL` here
 *  would produce `…/supplier-questionnaire-service/api/questionnaires/…`, a 404
 *  that reads like a routing bug.
 *
 *  Answers 201 with an empty body, so there is nothing to return — every caller
 *  invalidates its queries instead. */
export async function postResponses(
  requestId: string,
  body: { payload: SaveAnswerItem[] },
): Promise<void> {
  await apiFetch<void>(
    `/api/questionnaires/requests/${requestId}/responses`,
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/** The status transition migrated to
 *  `PATCH /api/questionnaires/:id/requests/:requestId/status` (modules
 *  architecture), so the questionnaire id moved from the body into the path and
 *  the base became `API_ROOT_URL`. Keeping `QUESTIONNAIRE_SERVICE_URL` here
 *  would produce `…/supplier-questionnaire-service/api/questionnaires/…`, a 404
 *  that reads like a routing bug.
 *
 *  Answers 200 with `{ data: <updated request> }`. Both callers discard it and
 *  invalidate their queries instead, so the return type stays `unknown`. */
export async function patchRequestStatus(
  questionnaireId: string,
  requestId: string,
  body: SubmitForReviewPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/api/questionnaires/${questionnaireId}/requests/${requestId}/status`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/** Returns the saved company-profile value for a given datapoint,
 *  scoped to a company. Used by the auto-fill-from-profile flow on the
 *  answer page: one call per datapoint-bound question, then the
 *  responses are POSTed via `postResponses` as `autoAnswered: true`.
 *
 *  Migrated to
 *  `GET /api/questionnaires/data-points/:dataPointId/companies/:companyId`
 *  (modules architecture), so the base became `API_ROOT_URL` and the body now
 *  arrives inside a `{ data }` envelope. Keeping `QUESTIONNAIRE_SERVICE_URL` here
 *  would produce `…/supplier-questionnaire-service/api/questionnaires/…`, a 404
 *  that reads like a routing bug. `useAutoFillFromProfile` unwraps the envelope.
 *
 *  Rejects with a 404 for an unrecognised datapoint or a company with no profile
 *  from the caller's perspective — the caller tolerates a per-call failure rather
 *  than failing the whole fan-out. */
export async function fetchCompanyDataPointAnswer(
  dataPointId: string,
  companyId: string,
): Promise<{ data: CompanyDataPointAnswer }> {
  return apiFetch<{ data: CompanyDataPointAnswer }>(
    `/api/questionnaires/data-points/${dataPointId}/companies/${companyId}`,
    {},
    API_ROOT_URL,
  );
}

/** Reviewer-side per-answer status update. Drives the per-question
 *  review badge (APPROVED / DENIED / IN_REVIEW / NOT_SUBMITTED) in
 *  both the review-per-company panel and the responder's read-only
 *  view of an IN_REVIEW request.
 *
 *  Migrated to `PATCH /api/questionnaires/requests/:requestId/questions/
 *  :questionId/response` (modules architecture), so the base became
 *  `API_ROOT_URL`. Keeping `QUESTIONNAIRE_SERVICE_URL` here would produce
 *  `…/supplier-questionnaire-service/api/questionnaires/…`, a 404 that reads
 *  like a routing bug.
 *
 *  Answers 204 with an empty body, so there is nothing to return — the caller
 *  invalidates its queries instead. */
export async function patchQuestionReviewStatus(
  requestId: string,
  questionId: string,
  status: 'APPROVED' | 'DENIED' | 'IN_REVIEW' | 'NOT_SUBMITTED',
): Promise<void> {
  await apiFetch<void>(
    `/api/questionnaires/requests/${requestId}/questions/${questionId}/response`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    API_ROOT_URL,
  );
}
